import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, ArrowLeft, Check, AlertCircle, HelpCircle, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPlaquePage = () => {
  const { user, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeValid, setCodeValid] = useState(null);
  const [codeError, setCodeError] = useState('');
  
  const [formData, setFormData] = useState({
    code: '',
    fullCode: '',
    reviewUrl: '',
    thresholdStars: '4',
    businessName: user?.business_name || ''
  });

  const verifyCode = async (code) => {
    if (code.length < 3) {
      setCodeValid(null);
      setCodeError('');
      return;
    }
    
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/plaques/verify/${code}`);
      const info = res.data;
      if (info.is_registered) {
        setCodeValid(false);
        setCodeError('This code is already registered');
      } else if (info.type === 'follow') {
        setCodeValid(false);
        setCodeError('This is a follow plaque. Please use the follow registration flow.');
      } else {
        setCodeValid(true);
        setCodeError('');
        setFormData(prev => ({ ...prev, fullCode: info.code }));
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setCodeValid(false);
        setCodeError('Invalid plaque code');
      } else {
        setCodeValid(false);
        setCodeError(error.response?.data?.detail || 'Error verifying code');
      }
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (formData.code) {
        verifyCode(formData.code);
      }
    }, 500);
    
    return () => clearTimeout(debounce);
  }, [formData.code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!codeValid) {
        toast.error('Please enter a valid plaque code');
        return;
      }
      setStep(2);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/plaques/register`, {
        code: formData.fullCode || formData.code.toUpperCase(),
        review_url: formData.reviewUrl,
        threshold_stars: parseInt(formData.thresholdStars),
        business_name: formData.businessName
      });
      
      toast.success('Plaque registered successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register plaque');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)] py-8 px-6">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[var(--color-text-secondary)] hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
          {user?.is_admin && !isImpersonating && (
            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors"
              data-testid="admin-dashboard-link"
            >
              <Shield className="w-4 h-4" />
              Admin Dashboard
            </Link>
          )}
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 1 ? 'bg-primary text-white' : 'bg-gray-200'
            }`}>
              {step > 1 ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <span className="text-sm font-medium hidden sm:inline">Enter Code</span>
          </div>
          <div className="flex-1 h-px bg-gray-200" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 2 ? 'bg-primary text-white' : 'bg-gray-200'
            }`}>
              2
            </div>
            <span className="text-sm font-medium hidden sm:inline">Configure</span>
          </div>
        </div>

        <Card className="shadow-layered border-0">
          <CardHeader>
            <CardTitle className="font-dashboard-heading text-xl font-bold">
              {step === 1 ? 'Enter Your Plaque Code' : 'Configure Your Plaque'}
            </CardTitle>
            <CardDescription>
              {step === 1 
                ? 'Enter the code printed on your rating plaque'
                : 'Set up where positive reviews should be directed'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 1 ? (
                <div className="space-y-2">
                  <Label htmlFor="code">Plaque Code</Label>
                  <div className="relative">
                    <Input
                      id="code"
                      type="text"
                      placeholder="e.g., 87XVH89VV"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      required
                      data-testid="plaque-code-input"
                      className="h-12 text-lg font-mono uppercase pr-12"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {verifying && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      )}
                      {!verifying && codeValid === true && (
                        <Check className="w-5 h-5 text-green-500" />
                      )}
                      {!verifying && codeValid === false && (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  </div>
                  {codeError && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {codeError}
                    </p>
                  )}
                  {codeValid && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Valid code! Click continue to set it up.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name</Label>
                    <Input
                      id="businessName"
                      type="text"
                      placeholder="Your Business Name"
                      value={formData.businessName}
                      onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                      required
                      data-testid="business-name-input"
                      className="h-11"
                    />
                    <p className="text-xs text-[var(--color-text-muted)]">
                      This will be displayed on the rating page
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="reviewUrl">Review Page URL</Label>
                    <Input
                      id="reviewUrl"
                      type="url"
                      placeholder="https://g.page/r/your-business/review"
                      value={formData.reviewUrl}
                      onChange={(e) => setFormData({ ...formData, reviewUrl: e.target.value })}
                      required
                      data-testid="review-url-input"
                      className="h-11"
                    />
                    <p className="text-xs text-[var(--color-text-muted)]">
                      The URL where happy customers will be redirected to leave a public review
                    </p>
                    <a 
                      href="https://www.reviewmybusiness.ie/help/reivewurl" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-[var(--color-accent)] hover:underline mt-1"
                      data-testid="review-url-help-link"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Don't know what to put here? Click here for help.
                    </a>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="threshold">Minimum Stars for Public Review</Label>
                    <Select
                      value={formData.thresholdStars}
                      onValueChange={(value) => setFormData({ ...formData, thresholdStars: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="threshold-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 stars or more</SelectItem>
                        <SelectItem value="4">4 stars or more</SelectItem>
                        <SelectItem value="5">5 stars only</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      Customers rating below this will leave private feedback instead
                    </p>
                  </div>
                </>
              )}
              
              <div className="flex gap-3">
                {step === 2 && (
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-11"
                  >
                    Back
                  </Button>
                )}
                <Button 
                  type="submit" 
                  className="flex-1 h-11 rounded-full btn-lift"
                  disabled={loading || (step === 1 && !codeValid)}
                  data-testid="register-plaque-submit"
                >
                  {loading ? 'Registering...' : step === 1 ? 'Continue' : 'Register Plaque'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPlaquePage;
